import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useMemo, useState } from "react";

import { Drawer, type DrawerBaseProps } from "@/components/Drawer/Drawer";
import { FormCheckboxGroup } from "@/components/Form/FormCheckbox";
import { filterProjectsByFilters } from "@/components/ProjectFilter/filterProjectsByFilters";
import { useProjectFilterOptions } from "@/components/ProjectFilter/useProjectFilterOptions";
import { TicketStatus } from "@/components/Ticket/TicketStatus";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Checkbox } from "@/design-system/Checkbox";
import { CheckboxMultiSelect } from "@/design-system/CheckboxMultiSelect";
import { FormField } from "@/design-system/FormField";
import { RatingBadge } from "@/design-system/RatingBadge";
import { Select } from "@/design-system/Select";
import { Stack } from "@/design-system/Stack";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useDebounceValue } from "@/hooks/useDebounce";
import {
  TICKET_ASSIGNEE_UNASSIGNED_ID,
  TicketAssigneeFilter,
  type TicketAssigneeFilterMode,
} from "@/modules/tickets/pages/list/components/TicketAssigneeFilter";
import { usePortfolioTicketsQueries } from "@/queries/portfolio-tickets/queryOptions";
import { ticketRatingValues, type TicketVisibility, ticketVisibilityValues } from "@/queries/tickets/constants";
import { useTranslation } from "@/translations";

/**
 * Filters shared by the portfolio ticket list and its route. When assigneeAccountIds are selected, assigneeMode
 * determines whether matching accounts are included in or excluded from the results.
 */
export type PortfolioTicketsFilterValues = {
  owner?: string[];
  maintenance?: string[];
  statusKeys?: string[];
  categoryNames?: string[];
  assigneeAccountIds?: string[];
  assigneeMode?: TicketAssigneeFilterMode;
  projectIds?: string[];
  ratings?: number[];
  isUnreadOnly?: boolean;
  visibility?: TicketVisibility;
};

type PortfolioTicketsFilterDrawerProps = DrawerBaseProps & {
  selectedFilter: PortfolioTicketsFilterValues;
  onUpdate: (value: PortfolioTicketsFilterValues) => void;
};

export function PortfolioTicketsFilterDrawer({
  selectedFilter,
  onUpdate,
  ...baseProps
}: PortfolioTicketsFilterDrawerProps): React.ReactNode {
  const [currFilter, setCurrFilter] = useState(selectedFilter);
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const { t } = useTranslation();
  const { data: linkedProjects = [] } = useLinkedProjects();

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setCurrFilter(selectedFilter);
  }, [selectedFilter]);

  const filteredProjects = useMemo(
    () =>
      filterProjectsByFilters(linkedProjects || [], {
        owner: currFilter.owner,
        maintenance: currFilter.maintenance,
      }),
    [linkedProjects, currFilter],
  );
  const { owners, maintainers, amountUniqueMaintainers, amountUniqueOwners } = useProjectFilterOptions(
    linkedProjects,
    currFilter,
  );
  const projectIds = useMemo(
    () =>
      filteredProjects
        .filter((project) => !currFilter.projectIds?.length || currFilter.projectIds.includes(project.id))
        .map((project) => project.id),
    [currFilter.projectIds, filteredProjects],
  );
  const scopedProjectIds = projectIds.length === linkedProjects.length ? undefined : projectIds;
  const debouncedAssigneeSearch = useDebounceValue(assigneeSearch.trim(), 300);
  const portfolioTicketQueries = usePortfolioTicketsQueries();
  const { data: filterOptions } = useQuery(
    portfolioTicketQueries.filterOptions({
      ProjectIds: scopedProjectIds,
      AssigneeSearch: debouncedAssigneeSearch || undefined,
      SelectedAssigneeAccountIds: currFilter.assigneeAccountIds?.filter((id) => id !== TICKET_ASSIGNEE_UNASSIGNED_ID),
      AssigneeLimit: 25,
    }),
  );
  const assigneeOptions = useMemo(
    () =>
      filterOptions?.assignees.map((assignee) => ({
        id: assignee.accountId,
        fullName: assignee.fullName,
        email: assignee.email,
      })) ?? [],
    [filterOptions?.assignees],
  );
  const statusOptions = useMemo(() => {
    const options = filterOptions?.statuses ?? [];
    const missingSelections = (currFilter.statusKeys ?? [])
      .filter((key) => !options.some((status) => status.key === key))
      .map((key) => ({ key, name: getStatusNameFromKey(key), color: undefined }));

    return [...options, ...missingSelections];
  }, [currFilter.statusKeys, filterOptions?.statuses]);
  const categoryOptions = useMemo(() => {
    const options = filterOptions?.categories ?? [];
    const missingSelections = (currFilter.categoryNames ?? []).filter((name) => !options.includes(name));

    return [...options, ...missingSelections];
  }, [currFilter.categoryNames, filterOptions?.categories]);

  const handleApplyFilter = () => {
    onUpdate({
      ...currFilter,
      statusKeys: currFilter.statusKeys,
      categoryNames: currFilter.categoryNames,
      assigneeAccountIds: currFilter.assigneeAccountIds,
      assigneeMode: currFilter.assigneeMode,
      maintenance: currFilter.maintenance,
      owner: currFilter.owner,
      projectIds: currFilter.projectIds,
      ratings: currFilter.ratings,
      isUnreadOnly: currFilter.isUnreadOnly,
      visibility: currFilter.visibility,
    });
  };

  const handleClearFilter = () => {
    onUpdate({
      ...currFilter,
      statusKeys: undefined,
      categoryNames: undefined,
      assigneeAccountIds: undefined,
      assigneeMode: undefined,
      maintenance: undefined,
      owner: undefined,
      projectIds: undefined,
      ratings: undefined,
      isUnreadOnly: undefined,
      visibility: undefined,
    });
  };

  return (
    <Drawer.Root title={t("page.portfolio.tickets.filter-drawer.title")} {...baseProps}>
      <Drawer.Body>
        <Stack gap="md">
          {linkedProjects && linkedProjects.length > 1 && (
            <>
              {amountUniqueOwners > 1 && (
                <FormField label={t("page.portfolio.tickets.filter.project-owner.label")}>
                  <CheckboxMultiSelect<string>
                    renderMoreSelected={(count) => t("common.label.x-more", { count })}
                    placeholder={t("page.portfolio.tickets.filter.project-owner.placeholder")}
                    renderOption={(x) => x}
                    keySelector={(x) => x}
                    items={owners}
                    selected={currFilter.owner}
                    onChange={(value) => setCurrFilter({ ...currFilter, owner: value })}
                  />
                </FormField>
              )}
              {amountUniqueMaintainers > 1 && (
                <FormField label={t("page.portfolio.tickets.filter.project-maintainer.label")}>
                  <CheckboxMultiSelect<string>
                    renderMoreSelected={(count) => t("common.label.x-more", { count })}
                    placeholder={t("page.portfolio.tickets.filter.project-maintainer.placeholder")}
                    renderOption={(x) => x}
                    keySelector={(x) => x}
                    items={maintainers}
                    selected={currFilter.maintenance}
                    onChange={(value) => setCurrFilter({ ...currFilter, maintenance: value })}
                  />
                </FormField>
              )}
              {filteredProjects.length > 1 && (
                <FormField label={t("page.portfolio.tickets.filter.project.label")}>
                  <CheckboxMultiSelect
                    renderMoreSelected={(count) => t("common.label.x-more", { count })}
                    selected={filteredProjects.filter((x) => currFilter.projectIds?.includes(x.id))}
                    items={filteredProjects}
                    keySelector={(x) => x.id}
                    renderOption={(x) => x.name}
                    onChange={(newSelectedProjects) => {
                      const selectedProjectIds =
                        newSelectedProjects.length === 0
                          ? []
                          : filteredProjects.filter((y) => newSelectedProjects.includes(y)).map((y) => y.id);

                      setCurrFilter({ ...currFilter, projectIds: selectedProjectIds });
                    }}
                    allSelectedText={t("page.tickets.header.filter.projects.all")}
                    placeholder={t("page.tickets.header.filter.projects")}
                  />
                </FormField>
              )}
            </>
          )}
          <FormField label={t("page.tickets.filter-drawer.assignee.label")}>
            <TicketAssigneeFilter
              items={assigneeOptions}
              selectedIds={currFilter.assigneeAccountIds}
              mode={currFilter.assigneeMode}
              onChange={(assigneeAccountIds) => setCurrFilter({ ...currFilter, assigneeAccountIds })}
              onModeChange={(assigneeMode) => setCurrFilter({ ...currFilter, assigneeMode })}
              onSearchChange={setAssigneeSearch}
            />
          </FormField>
          <FormField label={t("page.tickets.filter-drawer.status.label")}>
            <CheckboxMultiSelect
              renderMoreSelected={(count) => t("common.label.x-more", { count })}
              items={statusOptions}
              selected={statusOptions.filter((status) => currFilter.statusKeys?.includes(status.key))}
              onChange={(statuses) => setCurrFilter({ ...currFilter, statusKeys: statuses.map(({ key }) => key) })}
              placeholder={t("page.tickets.filter-drawer.status.placeholder")}
              renderOption={(status) =>
                status.color ? (
                  <TicketStatus labelColor={status.color} description={status.name} variant="filled" />
                ) : (
                  <Badge variant="default">{status.name}</Badge>
                )
              }
              keySelector={(status) => status.key}
            />
          </FormField>
          <FormField label={t("page.tickets.filter-drawer.category.label")}>
            <CheckboxMultiSelect
              renderMoreSelected={(count) => t("common.label.x-more", { count })}
              items={categoryOptions}
              selected={categoryOptions.filter((category) => currFilter.categoryNames?.includes(category))}
              onChange={(categoryNames) => setCurrFilter({ ...currFilter, categoryNames })}
              placeholder={t("page.tickets.filter-drawer.category.placeholder")}
              renderOption={(category) => category}
              keySelector={(category) => category}
            />
          </FormField>
          <FormField label={t("page.portfolio.tickets.filter.rating.label")}>
            <CheckboxMultiSelect
              renderMoreSelected={(count) => t("common.label.x-more", { count })}
              items={ticketRatingValues}
              selected={ticketRatingValues.filter((x) => currFilter.ratings?.includes(x))}
              onChange={(x) => setCurrFilter({ ...currFilter, ratings: x })}
              placeholder={t("page.portfolio.tickets.filter.rating.placeholder")}
              renderOption={(x) => {
                if (x === -1) {
                  return t("page.portfolio.tickets.filter.rating.not-rated");
                }

                return <RatingBadge rating={x} />;
              }}
              keySelector={(x) => x}
            />
          </FormField>
          <FormField label={t("page.portfolio.tickets.filter.visibility.label")}>
            <Select
              items={ticketVisibilityValues}
              selected={currFilter.visibility}
              emptyItem={t("common.label.ticket-visibility.all")}
              onChange={(x) => setCurrFilter({ ...currFilter, visibility: x })}
              renderOption={(x) => {
                switch (x) {
                  case "private":
                    return t("common.label.ticket-visibility.private");
                  case "collective":
                    return t("common.label.ticket-visibility.collective");
                }
              }}
              keySelector={(x) => x}
            />
          </FormField>
          <FormCheckboxGroup layout="vertical">
            <Checkbox
              label={t("page.tickets.header.filter.unread")}
              name="tickets_filter_unread"
              checked={currFilter.isUnreadOnly}
              onChange={(e) => setCurrFilter({ ...currFilter, isUnreadOnly: e.target.checked })}
            />
          </FormCheckboxGroup>
        </Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.Close>
          <Button variant="subtle" onClick={handleClearFilter}>
            {t("common.action.clear")}
          </Button>
        </Drawer.Close>
        <Drawer.Close>
          <Button onClick={handleApplyFilter}>{t("common.action.apply")}</Button>
        </Drawer.Close>
      </Drawer.Actions>
    </Drawer.Root>
  );
}

function getStatusNameFromKey(key: string): string {
  const separatorIndex = key.indexOf(":");
  if (separatorIndex < 0) {
    return key;
  }

  try {
    return decodeURIComponent(key.slice(separatorIndex + 1));
  } catch {
    return key;
  }
}
